import { constructValue } from '@adornis/baseql/entities/construct.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { generateStatisticPdf } from '@adornis/buchungsheld-db/api/statistic.js';
import { Participation } from '@adornis/buchungsheld-db/entities/Participation.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import '@adornis/charts/apexcharts/client/apex-chart.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import type { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { FilterEditorOptions } from '@adornis/filter/filter-options.js';
import { buildFullOptions } from '@adornis/filter/filter-options.js';
import { openPopupWithLoadingScreen } from '@adornis/print/client/safe-popup.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { BehaviorSubject, combineLatest, filter, map } from 'rxjs';

/**
 * @element bh-page-admin-global-statistics
 */
@customElement('bh-page-admin-global-statistics')
export class BHPageAdminGlobalStatistics extends BuchungsheldLitElement {
  protected readonly refresh = new BehaviorSubject(1);
  protected readonly _filter = new RXController(
    this,
    combineLatest([this.refresh]).pipe(
      filter(([institutionID]) => !!institutionID),
      map(() => {
        const filter = this.filter as BHFilter;
        // filter.timeframeID = timeframeID;
        return filter;
      }),
    ),
  );

  get filterKey() {
    return `statisics:global:filter`;
  }

  get filter() {
    return localStorage.getItem(this.filterKey)
      ? constructValue(JSON.parse(localStorage.getItem(this.filterKey)!))
      : new BHFilter({});
  }

  setFilter(filter: AdornisFilter) {
    localStorage.setItem(this.filterKey, JSON.stringify(filter.toJSON()));
    this.refresh.next(1);
  }

  get filterOptions() {
    const filterOptions = buildFullOptions({
      fieldAspectGeneration: {
        class: Participation,
        shouldIncludeField: (baseQLFieldDefinition, path, baseEntity) => {
          return shouldIncludeFieldHelpers.bySelectionSet({
            whitelist: {
              state: 1,
              isNoShow: 1,
              product: {
                name: 1,
                category: { name: 1 },
              },
            } satisfies AccessControlSelectionSet<Participation>,
          })(baseQLFieldDefinition, path, baseEntity);
        },
      },
      filter: this.filter,
      onFilterChanged: filter => {
        this.setFilter(filter);
      },
    } satisfies FilterEditorOptions<typeof Participation>);

    filterOptions.searchable = false;

    return filterOptions;
  }

  override render() {
    return html`
      <x-flex space="xl">
        <!-- filter -->
        <x-flex vertical space="xs">
          <x-flex horizontal ${css({ justifyContent: 'space-between', alignItems: 'center' })}>
            <x-h2> Filter </x-h2>
            <!-- download pdf -->
            <tr-button mode="outline" icon="download" @click=${() => this.downloadPDF()}> Download </tr-button>
          </x-flex>
          <x-filter-editor .options=${this.filterOptions}></x-filter-editor>
        </x-flex>

        <!-- stats -->
        ${this.renderStats()}
      </x-flex>
    `;
  }

  protected renderStats() {
    return this._filter.value
      ? html` <global-instititution-statistics .filter=${this._filter.value}></global-instititution-statistics> `
      : html`<x-loader></x-loader>`;
  }

  async downloadPDF() {
    try {
      const filter = this._filter.value;
      if (!filter) throw new Error('no filter');

      const getLink = generateStatisticPdf(filter)();
      await openPopupWithLoadingScreen(getLink);
    } catch (err) {
      console.error(err);
      XSnackbar.show(this._translation.translate('err', 'could_not_process_data'));
    }
  }
}
